import * as React from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { useNavigate } from "react-router-dom";
import { MgpButton, MgpDD, MgpTextbox } from "../components/global";
import { MgpPaper } from "../components/global/MgpPaper";
import { MgpBackdrop } from "../components/global/MgpBackdrop";
import { Box, Container, FormControl, FormLabel, Grid } from "@mui/material";
import * as yup from "yup";
import { Formik } from "formik";
import { useCountry, usePatient } from "../hooks";
import MgpAlertBar from "../components/global/MgpAlertBar";
import { StorageHelper } from "../helpers";
import {
  findCountryByCode,
  getGccCountryType,
  getNationalIdFieldConfig,
  resolveResidenceNationality,
  sanitizeNationalIdInput,
  validateNationalIdValue,
} from "../helpers/gccNationalId";
import { Stack } from "@mui/system";
import { colors } from "../theme/colors";

const PATIENT_CONTEXT_STORAGE_KEY = "patientRegistrationContext";
const ID_ISSUING_COUNTRY_HELPER_TEXT =
  "Select the country that issued the patient's National ID or Residence ID. This determines the required ID format.";
const normalizePhoneCountryCode = (countryCode) => {
  const code = String(countryCode ?? "").trim().toLowerCase();
  return /^[a-z]{2}$/.test(code) ? code : "";
};
const idTypeOptionSx = (selected, disabled = false) => ({
  m: 0,
  px: 1.25,
  py: 0.55,
  flex: "1 1 0",
  minWidth: 0,
  minHeight: 38,
  boxSizing: "border-box",
  border: "1px solid",
  borderColor: selected ? colors.PRIMARY : colors.DEVIDER,
  borderRadius: "8px",
  backgroundColor: selected ? colors.CRFBGPROGRESS : colors.WHITE,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  transition: "border-color 160ms ease, background-color 160ms ease",
  "&:hover": {
    borderColor: disabled ? colors.DEVIDER : colors.PRIMARY,
    backgroundColor: disabled
      ? selected
        ? colors.CRFBGPROGRESS
        : colors.WHITE
      : selected
      ? colors.CRFBGPROGRESS
      : colors.CRFBG,
  },
  "& .MuiRadio-root": {
    p: 0.5,
    color: colors.MENULISTSECONDARY,
    "&.Mui-checked": {
      color: colors.PRIMARY,
    },
  },
  "& .MuiFormControlLabel-label": {
    fontSize: "13px",
    fontWeight: selected ? 500 : 400,
    color: colors.PRIMARY_TEXT,
    lineHeight: 1.25,
    whiteSpace: "normal",
  },
});

export const VerifyPatient = () => {
  const navigate = useNavigate();
  const { useNationalIdCheck, useAddPatientWithEncounter } = usePatient();
  const { useGetGccCountries, useGetCountries } = useCountry();
  const [IsNationalIdExists] = useNationalIdCheck;
  const [AddPatientWithEncounter] = useAddPatientWithEncounter;
  const loggedInUser = React.useMemo(
    () => JSON.parse(StorageHelper.get("user")) ?? {},
    []
  );
  const isPiiLessUser = loggedInUser.collect_pii === false;
  const registrationStartedRef = React.useRef(false);
  const [fetchGccCountries, { data: gccData, loading: gccLoading }] =
    useGetGccCountries;
  const [fetchCountries, { data: countriesData }] = useGetCountries;
  const [gccCountryOptions, setGccCountryOptions] = React.useState([]);

  const getCountryDisplayName = React.useCallback((country) => {
    return String(country?.country_name || "").trim();
  }, []);

  const sortCountries = React.useCallback((countries) => {
    return [...countries].sort((a, b) =>
      getCountryDisplayName(a).localeCompare(getCountryDisplayName(b))
    );
  }, [getCountryDisplayName]);

  React.useEffect(() => {
    if (!isPiiLessUser) fetchGccCountries();
  }, [fetchGccCountries, isPiiLessUser]);

  React.useEffect(() => {
    const countries = gccData?.GetGccCountries ?? [];
    if (countries.length > 0) {
      setGccCountryOptions(sortCountries(countries));
    } else if (!gccLoading && gccData) {
      fetchCountries({ variables: { isGcc: true } });
    }
  }, [gccData, gccLoading, fetchCountries, sortCountries]);

  React.useEffect(() => {
    const countries = countriesData?.GetCountries ?? [];
    if (countries.length > 0 && gccCountryOptions.length === 0) {
      setGccCountryOptions(sortCountries(countries));
    }
  }, [countriesData, gccCountryOptions.length, sortCountries]);

  const countrySelectOptions = React.useMemo(
    () =>
      gccCountryOptions
        .filter((c) => c.collect_pii !== false)
        .map((c) => ({
          label: getCountryDisplayName(c),
          value: String(c.country_code ?? "").trim(),
        })),
    [gccCountryOptions, getCountryDisplayName]
  );

  const userResidenceCountryCode = React.useMemo(() => {
    const userCountryName = String(loggedInUser.hospital_country_name ?? "")
      .trim()
      .toLowerCase();
    if (!userCountryName) return "";
    return (
      countrySelectOptions.find(
        (option) => option.label.toLowerCase() === userCountryName
      )?.value ?? ""
    );
  }, [loggedInUser.hospital_country_name, countrySelectOptions]);
  const isResidenceCountryLocked = Boolean(userResidenceCountryCode);

  const formInitialValues = React.useMemo(
    () => ({
      countryCode: "",
      residenceCountryCode: userResidenceCountryCode,
      isCitizen: false,
      national: "",
      confirmNational: "",
    }),
    [userResidenceCountryCode]
  );

  const modalMenuProps = React.useMemo(
    () => ({
      autoFocus: false,
      disableScrollLock: true,
      PaperProps: {
        component: MgpPaper,
        sx: {
          zIndex: 2000,
          maxHeight: 280,
          backgroundColor: "#ffffff",
          "&.MuiPaper-root": {
            backgroundColor: "#ffffff",
          },
          "& .MuiMenu-list": {
            backgroundColor: "#ffffff",
          },
          "& .MuiListSubheader-root": {
            backgroundColor: "#ffffff",
          },
          "& .MuiMenuItem-root": {
            backgroundColor: "#ffffff",
          },
        },
      },
      sx: { zIndex: 2000 },
    }),
    []
  );
  const handleSubmitClose = () => {
    setSubmitOpen(false);
  };
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [alertOption, setAlertOption] = React.useState(false);
  const [submitOpen, setSubmitOpen] = React.useState(false);
  const verifyPatient = () => {
    if (nationalID.national !== nationalID.confirmNational) {
      setNationaID({ ...nationalID, errors: "National ID not matching" });
    } else {
      handleOpenBackdrop();
    }
  };
  const handleCloseBackdrop = () => {
    setOpenBackdrop(false);
  };
  const handleOpenBackdrop = () => {
    setOpenBackdrop(true);
  };

  const [nationalID, setNationaID] = React.useState({
    national: "",
    confirmNational: "",
    errors: "",
  });

  const handleSubmitCloseAlert = () => {
    setAlertOption(false);
    setMessage("");
  };
  const handleChange = (e) => {
    setNationaID({
      ...nationalID,
      [e.target.name]: e.target.value,
      errors: "",
    });
  };
  const handlePaste = (event) => {
    // For preventing copy paste
    event.preventDefault();
  };

  const validationSchema = React.useMemo(
    () =>
      yup.object().shape({
        countryCode: yup.string().required("ID Issuing Country is required"),
        residenceCountryCode: yup
          .string()
          .required("Country of residence is required"),
        national: yup
          .string()
          .required("Required")
          .test("gcc-national-id", function (value) {
            const { countryCode, isCitizen } = this.parent;
            const country = findCountryByCode(gccCountryOptions, countryCode);
            const result = validateNationalIdValue(value, country, isCitizen);
            return result.valid
              ? true
              : this.createError({ message: result.message });
          }),
        confirmNational: yup
          .string()
          .oneOf([yup.ref("national")], "National IDs should match")
          .required("Required"),
      }),
    [gccCountryOptions]
  );

  const registerPiiLessPatient = React.useCallback(() => {
    setOpenBackdrop(true);
    AddPatientWithEncounter({
      variables: {
        input: { Demographic: { is_draft: true } },
        encounterDetails: { basicVisit: { typeofvisit: "Baseline Visit" } },
        encounterStatus: 0,
      },
      onCompleted: (res) => {
        const encounterId =
          res?.AddPatientWithEncounter?.encounter?.encounter_id;
        setOpenBackdrop(false);
        if (encounterId) {
          navigate(`/app/patients/visitHistory/${encounterId}`);
        } else {
          setMessage("Something went wrong");
          setAlertOption(true);
        }
      },
      onError: (error) => {
        setOpenBackdrop(false);
        setMessage(error?.message || "Something went wrong");
        setAlertOption(true);
      },
    });
  }, [AddPatientWithEncounter, navigate]);

  React.useEffect(() => {
    if (!isPiiLessUser || registrationStartedRef.current) return;
    registrationStartedRef.current = true;
    registerPiiLessPatient();
  }, [isPiiLessUser, registerPiiLessPatient]);

  if (isPiiLessUser) {
    return (
      <Container fluid="true">
        {openBackdrop || !registrationStartedRef.current ? (
          <MgpBackdrop
            openBackdrop
            message={"Generating registry number..."}
          />
        ) : (
          <Stack spacing={2} justifyContent={"center"} direction={"row"}>
            <MgpButton variant="contained" onClick={registerPiiLessPatient}>
              Retry
            </MgpButton>
          </Stack>
        )}
        <MgpAlertBar
          open={alertOption}
          setOpen={handleSubmitCloseAlert}
          alertType="error"
          message={message}
        />
      </Container>
    );
  }

  return (
    <Container fluid="true">
      <Formik
        initialValues={formInitialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(payload) => {
          handleOpenBackdrop();
          IsNationalIdExists({
            variables: {
              nationalId: payload["confirmNational"].toString(),
              countryCode: payload.countryCode || undefined,
              isCitizen: payload.isCitizen,
            },
            onCompleted: (data) => {
              if (data?.IsNationalIdExists?.patient_id == -1) {
                setMessage(
                  "National ID is not a valid number. Please check again!"
                );
                setAlertOption(true);
                handleCloseBackdrop();
              } else if (data?.IsNationalIdExists) {
                const loggedUser = JSON.parse(
                  StorageHelper.get("user")
                )?.user_id;
                setMessage("National ID already registered.");
                setAlertOption(true);
                handleCloseBackdrop();
                if (data?.IsNationalIdExists?.user_id == loggedUser) {
                  // If the national id is registred with the logged in user then redirecting to visit history page
                  navigate(
                    `/app/patients/viewPatient/${data?.IsNationalIdExists?.patient_id}`
                  );
                }
              } else {
                const selectedCountry = findCountryByCode(
                  gccCountryOptions,
                  payload.countryCode
                );
                const selectedResidenceCountry = findCountryByCode(
                  gccCountryOptions,
                  payload.residenceCountryCode
                );
                const countryOfResidenceName =
                  selectedResidenceCountry?.country_name?.trim() || "";
                const phoneCountryCode = normalizePhoneCountryCode(
                  selectedResidenceCountry?.phone_country_code
                );
                const { nationality, isCitizen, countryOfOrigin } =
                  resolveResidenceNationality({
                    issuingCountry: selectedCountry,
                    residenceCountry: selectedResidenceCountry,
                    isCitizen: payload.isCitizen,
                  });

                window.sessionStorage.setItem(
                  PATIENT_CONTEXT_STORAGE_KEY,
                  JSON.stringify({
                    nationalId: payload.confirmNational,
                    nationality,
                    isCitizen,
                    countryList: countryOfOrigin,
                    countryOfResidence: countryOfResidenceName,
                    idIssuingCountry:
                      selectedCountry?.country_name?.trim() || "",
                    phoneCountryCode,
                  })
                );

                navigate(
                  `/app/patients/newPatient/${encodeURIComponent(
                    payload.confirmNational
                  )}`
                );
              }
            },
          });
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          touched,
          values,
          resetForm,
        }) => {
          const selectedCountry = findCountryByCode(
            gccCountryOptions,
            values.countryCode
          );
          const nationalIdConfig = getNationalIdFieldConfig(
            selectedCountry,
            values.isCitizen
          );
          const nationalFieldsDisabled = !values.countryCode || gccLoading;
          const isSaudiIssuingCountry =
            getGccCountryType(selectedCountry) === "SA";
          const nationalIdPrefix = String(values.national ?? "").trim()[0];
          const saudiIdType =
            isSaudiIssuingCountry && nationalIdPrefix === "1"
              ? "citizen"
              : isSaudiIssuingCountry && nationalIdPrefix === "2"
              ? "resident"
              : "";
          const isCitizenDisabled = saudiIdType === "resident";
          const isResidentDisabled = saudiIdType === "citizen";

          const handleNationalIdInput = (fieldName, rawValue) => {
            const sanitized = sanitizeNationalIdInput(
              rawValue,
              nationalIdConfig
            );
            setFieldValue(fieldName, sanitized);
            if (fieldName === "national" && isSaudiIssuingCountry) {
              if (sanitized.startsWith("1")) {
                setFieldValue("isCitizen", true);
              } else if (sanitized.startsWith("2")) {
                setFieldValue("isCitizen", false);
              }
            }
          };

          return (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#ffffff",
                        },
                        "& .MuiSelect-select": {
                          backgroundColor: "#ffffff",
                        },
                      }}
                    >
                      <MgpDD
                        label="ID Issuing Country"
                        variant="outlined"
                        size="small"
                        name="countryCode"
                        fullWidth
                        options={countrySelectOptions}
                        value={values.countryCode}
                        multiselect={false}
                        disabled={
                          gccLoading || countrySelectOptions.length === 0
                        }
                        error={Boolean(
                          touched.countryCode && errors.countryCode
                        )}
                        helperText={
                          (touched.countryCode && errors.countryCode) ||
                          ID_ISSUING_COUNTRY_HELPER_TEXT
                        }
                        onBlur={handleBlur}
                        onChange={(e) => {
                          const countryCode = String(
                            e.target.value ?? ""
                          ).trim();
                          handleChange(e);
                          setFieldValue("countryCode", countryCode);
                          setFieldValue("national", "");
                          setFieldValue("confirmNational", "");
                        }}
                        MenuProps={modalMenuProps}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth sx={{ pt: 0.1 }}>
                      <FormLabel
                        component="legend"
                        sx={{
                          color: colors.PRIMARY_TEXT,
                          fontSize: "13px",
                          fontWeight: 500,
                          mb: 0.5,
                          "&.Mui-focused": {
                            color: colors.PRIMARY_TEXT,
                          },
                        }}
                      >
                        ID Type
                      </FormLabel>
                      <RadioGroup
                        row
                        name="idType"
                        value={values.isCitizen ? "citizen" : "resident"}
                        onChange={(e) => {
                          const isCitizen = e.target.value === "citizen";
                          if (
                            (isCitizen && isCitizenDisabled) ||
                            (!isCitizen && isResidentDisabled)
                          ) {
                            return;
                          }
                          setFieldValue("isCitizen", isCitizen);
                          setFieldValue("national", "");
                          setFieldValue("confirmNational", "");
                        }}
                        sx={{
                          alignItems: "stretch",
                          gap: 1,
                          flexWrap: "wrap",
                          width: "100%",
                        }}
                      >
                        <FormControlLabel
                          value="citizen"
                          control={<Radio size="small" />}
                          label="Citizen (National ID)"
                          disabled={isCitizenDisabled}
                          sx={idTypeOptionSx(
                            values.isCitizen,
                            isCitizenDisabled
                          )}
                        />
                        <FormControlLabel
                          value="resident"
                          control={<Radio size="small" />}
                          label="Resident (Residence ID)"
                          disabled={isResidentDisabled}
                          sx={idTypeOptionSx(
                            !values.isCitizen,
                            isResidentDisabled
                          )}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={12}>
                <Box
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiSelect-select": {
                      backgroundColor: "#ffffff",
                    },
                  }}
                >
                  <MgpDD
                    label="Country of Residence"
                    variant="outlined"
                    size="small"
                    name="residenceCountryCode"
                    fullWidth
                    options={countrySelectOptions}
                    value={values.residenceCountryCode}
                    multiselect={false}
                    disabled={
                      isResidenceCountryLocked ||
                      gccLoading ||
                      countrySelectOptions.length === 0
                    }
                    error={Boolean(
                      touched.residenceCountryCode &&
                        errors.residenceCountryCode
                    )}
                    helperText={
                      (touched.residenceCountryCode &&
                        errors.residenceCountryCode) ||
                      (isResidenceCountryLocked
                        ? "Set to your hospital's country. Patient nationality is based on this country."
                        : "")
                    }
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue(
                        "residenceCountryCode",
                        String(e.target.value ?? "").trim()
                      );
                    }}
                    MenuProps={modalMenuProps}
                  />
                </Box>
              </Grid>
              <Grid item md={12}>
                <MgpTextbox
                  fullWidth
                  size="small"
                  label="National ID"
                  name="national"
                  value={values["national"]}
                  disabled={nationalFieldsDisabled}
                  inputProps={{
                    maxLength: nationalIdConfig.maxLength,
                    fontSize: "13px",
                    fontWeight: "400",
                    inputMode: "numeric",
                  }}
                  onChange={(e) =>
                    handleNationalIdInput("national", e.target.value)
                  }
                  onPaste={handlePaste}
                  onBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  variant="outlined"
                  error={touched["national"] && !!errors["national"]}
                  helperText={
                    (touched["national"] && errors["national"]) ||
                    (values.countryCode &&
                      nationalIdConfig.type &&
                      nationalIdConfig.hint) ||
                    ""
                  }
                />
              </Grid>
              <Grid item md={12}>
                <MgpTextbox
                  fullWidth
                  size="small"
                  label="Confirm National ID"
                  name="confirmNational"
                  value={values["confirmNational"]}
                  disabled={nationalFieldsDisabled}
                  onPaste={handlePaste}
                  onChange={(e) =>
                    handleNationalIdInput("confirmNational", e.target.value)
                  }
                  onBlur={handleBlur}
                  inputProps={{
                    maxLength: nationalIdConfig.maxLength,
                    fontSize: "13px",
                    fontWeight: "400",
                    inputMode: "numeric",
                  }}
                  setFieldValue={setFieldValue}
                  variant="outlined"
                  error={
                    touched["confirmNational"] && !!errors["confirmNational"]
                  }
                  helperText={
                    touched["confirmNational"] && errors["confirmNational"]
                  }
                />
              </Grid>
              <Grid item md={3}></Grid>
              <Grid item md={6}>
                <Stack spacing={2} justifyContent={"center"} direction={"row"}>
                  <MgpButton
                    disabled={
                      !values.countryCode ||
                      !values.residenceCountryCode ||
                      values["national"] === "" ||
                      values["confirmNational"] === "" ||
                      gccLoading
                    }
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    Continue to Register
                  </MgpButton>
                </Stack>
              </Grid>
              <Grid item md={3}></Grid>
              {openBackdrop && (
                <Grid item md={12}>
                  <MgpBackdrop
                    openBackdrop={openBackdrop}
                    message={"Verifying..."}
                  />
                </Grid>
              )}
            </Grid>
          </form>
          );
        }}
      </Formik>
      <MgpAlertBar
        open={alertOption}
        setOpen={handleSubmitCloseAlert}
        alertType="error"
        message={message}
      />
    </Container>
  );
};
